import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProjectCard from "../components/ProjectCard";
import ProjectHero from "../components/ProjectHero";
import ProjectAbout from "../components/ProjectAbout";
import ContactForm from "../components/dashboard components/forms/ContactForm"; // ✅ Imported ContactForm

// ✅ Environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OurProjects = () => {
  const navigate = useNavigate();
  const [ongoingCards, setOngoingCards] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [heroData, setHeroData] = useState(null);
  const [projectAbout, setProjectAbout] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false); // ✅ Popup state

  useEffect(() => {
    fetchHeroData();
    fetchProjectAbout();
    fetchOngoingCards();
    fetchCompletedProjects();
  }, []);

  // Fetch hero, about, ongoing, completed (same as before)
  const fetchHeroData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/hero`);
      console.log("hero res", res);
      setHeroData(res.data[0] || null);
    } catch (err) {
      console.error("❌ Error fetching hero:", err);
    }
  };

  const fetchProjectAbout = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/about`);
      setProjectAbout(res.data[0] || null);
    } catch (err) {
      console.error("❌ Error fetching about:", err);
    }
  };

  const fetchOngoingCards = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/ongoing`);
      setOngoingCards(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching ongoing:", err);
    }
  };

  const fetchCompletedProjects = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/completed-cards`);
      setCompletedProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching completed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ✅ Hero Section (component) */}
      <ProjectHero heroData={heroData} API_BASE_URL={API_BASE_URL} />

      {/* ✅ About Section (component) */}
      <ProjectAbout projectAbout={projectAbout} />

      {/* Ongoing Projects */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-full mb-4">
              Current Developments
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Ongoing Projects
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ongoingCards.map((project) => (
              <ProjectCard
                key={project.project_id || project.id}
                project={project}
              />
            ))}
            {ongoingCards.length === 0 && (
              <p className="text-gray-500 col-span-full text-center">
                No ongoing projects available right now.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Completed Projects */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              Successfully Delivered
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Completed Projects
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {completedProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
            {completedProjects.length === 0 && (
              <p className="text-gray-500 col-span-full text-center">
                No completed projects to show.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-br from-green-100 to-blue-100">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">
            Interested in Our Projects?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
            Get in touch with our team to learn more about available properties
            and investment opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            {/* ✅ Popup trigger */}
            <button
              onClick={() => setShowContactForm(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:from-blue-600 hover:to-blue-700 transition-colors shadow-md sm:shadow-lg"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* ✅ Popup Modal for ContactForm */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <ContactForm onSuccess={() => setShowContactForm(false)} />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default OurProjects;
